import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';


// Type for the beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// App version - update this when releasing new APK
const APP_VERSION = '1.1';
const APK_FILENAME = `giggo-waiter-v${APP_VERSION}.apk`;

export const Landing: React.FC = () => {
  const navigate = useNavigate();

  const [isClearing, setIsClearing] = useState(false);
  const [clearMessage, setClearMessage] = useState<string | null>(null);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isNativeApp, setIsNativeApp] = useState(false);

  useEffect(() => {
    // Check if running inside native Android app
    if (typeof (window as any).Android !== 'undefined') {
      setIsNativeApp(true);
    }

    // Check if already running as installed PWA
    if (window.matchMedia('(display-mode: fullscreen)').matches ||
        window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Capture the install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Detect when app gets installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setInstallPrompt(null);
    }
  };

  const handleClearCache = async () => {
    setIsClearing(true);
    setClearMessage(null);

    try {
      // Clear localStorage
      localStorage.clear();

      // Clear sessionStorage
      sessionStorage.clear();

      // Clear service worker caches if available
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      // Unregister service workers if any
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }

      setClearMessage('הנתונים נוקו בהצלחה');

      // Reload after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Failed to clear cache:', error);
      setClearMessage('שגיאה בניקוי הנתונים');
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4 watchlg:p-3 watch:p-2">
      <div className="max-w-md w-full text-center">
        <h1 className="text-4xl watchlg:text-3xl watch:text-2xl font-bold mb-2 watch:mb-1">Giggo</h1>
        <p className="text-gray-300 mb-8 watchlg:mb-6 watch:mb-4 watchlg:text-base watch:text-sm">מערכת ניהול אירועים</p>

        <div className="space-y-4 watchlg:space-y-3 watch:space-y-2">
          <button
            onClick={() => navigate('/admin')}
            className="btn-primary w-full text-lg watchlg:text-base py-4 watchlg:py-3 watch:py-3 watch:text-base"
          >
            מנהל
          </button>

          <button
            onClick={() => navigate('/waiter')}
            className="btn-secondary w-full text-lg watchlg:text-base py-4 watchlg:py-3 watch:py-3 watch:text-base"
          >
            מלצר
          </button>

          <button
            onClick={() => navigate('/kitchen')}
            className="btn-secondary w-full text-lg watchlg:text-base py-4 watchlg:py-3 watch:py-3 watch:text-base"
          >
            מטבח
          </button>
        </div>

        {/* PWA Install Button */}
        {installPrompt && !isInstalled && (
          <div className="mt-6 watchlg:mt-4 watch:mt-3">
            <button
              onClick={handleInstall}
              className="w-full bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white font-bold py-4 px-6 watchlg:py-3 watchlg:px-4 watchlg:text-base watch:py-3 watch:px-3 rounded-xl transition-colors text-lg watch:text-sm flex items-center justify-center gap-3 watchlg:gap-2 watch:gap-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              התקן אפליקציה (מסך מלא)
            </button>
            <p className="text-gray-400 text-sm mt-2">
              מומלץ למלצרים - פועל ללא סרגל דפדפן
            </p>
          </div>
        )}

        {isInstalled && (
          <div className="mt-6 text-green-400 text-sm flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            האפליקציה מותקנת
          </div>
        )}

        {/* Android APK Download Button - hidden when already in native app */}
        {!isNativeApp && (
          <div className="mt-6 watchlg:mt-4 watch:mt-3">
            <a
              href={`/${APK_FILENAME}`}
              download
              className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-bold py-4 px-6 watchlg:py-3 watchlg:px-4 watchlg:text-base watch:py-3 watch:px-3 rounded-xl transition-colors text-lg watch:text-sm flex items-center justify-center gap-3 watchlg:gap-2 watch:gap-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              הורד אפליקציית אנדרואיד
            </a>
            <p className="text-gray-400 text-sm mt-2">
              גרסה {APP_VERSION} • מומלץ לשעונים חכמים
            </p>
          </div>
        )}

        {/* Show native app indicator */}
        {isNativeApp && (
          <div className="mt-6 text-green-400 text-sm flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            פועל באפליקציית Giggo
          </div>
        )}

        <div className="mt-8 watchlg:mt-4 watch:mt-3 text-sm text-gray-400">
          <p>גרסה {APP_VERSION}</p>
        </div>

        {/* Clear Cache Button */}
        <div className="mt-6 watchlg:mt-4 watch:mt-3 pt-6 watchlg:pt-4 watch:pt-3 border-t border-gray-700">
          <button
            onClick={handleClearCache}
            disabled={isClearing}
            className="text-gray-400 hover:text-red-400 text-sm flex items-center justify-center gap-2 mx-auto transition-colors disabled:opacity-50"
          >
            {isClearing ? (
              <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
            נקה זיכרון מקומי
          </button>
          {clearMessage && (
            <p className={`mt-2 text-sm ${clearMessage.includes('שגיאה') ? 'text-red-400' : 'text-green-400'}`}>
              {clearMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
